import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import AppHeader from '../components/layout/AppHeader';
import AppFooter from '../components/layout/AppFooter';
import InquiryButton from '../components/ui/inquiry/inquiryButton';
import Main from '../pages/main';
import Company from '../pages/company';
import Bus from '../pages/bus';
import EstimateList from '../pages/estimateList';
import EstimateDetail from '../pages/estimateDetail';
import MyEstimateSearch from '../pages/myEstimateSearch';
import MyEstimateList from '../pages/myEstimateList';
import ServiceCenter from '../pages/serviceCenter';
import Privacy from '../pages/privacy';
import Terms from '../pages/terms';
import NotFoundPage from '../pages/notFoundPage';
import EnMain from '../pages/en/EnMain';
import EnBus from '../pages/en/EnBus';
import EnCompany from '../pages/en/EnCompany';
import EnCenter from '../pages/en/EnCenter';
import { isEnglishPath } from '../pages/en/enCommon';

/** 예전 견적목록 주소(/estimate/list/3)를 새 주소(/list/3)로 넘긴다. */
function LegacyRedirect({ to }) {
  const { page } = useParams();
  return <Navigate to={`${to}/${page ?? 1}`} replace />;
}

/** 영문 주소(/en…)면 문서 언어를 en 으로, 아니면 ko 로 — 화면 읽기 프로그램·번역기·구글이 본다 (2026-11-04) */
function LangSync() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    document.documentElement.lang = isEnglishPath(pathname) ? 'en' : 'ko';
  }, [pathname]);
  return null;
}

/** 한국어 상담 버튼(카카오톡·전화)은 영문 화면에서 빼고 영어 문의 양식으로 보낸다 */
function KoreanOnly({ children }) {
  const { pathname } = useLocation();
  return isEnglishPath(pathname) ? null : children;
}

/** PC/모바일 공통 단일 라우터. 기기 판별 없이 화면 폭으로만 레이아웃이 바뀐다. */
export default function AppRouter({ companyData, ExceptionHandler }) {
  return (
    <ErrorBoundary FallbackComponent={ExceptionHandler}>
      <BrowserRouter>
        <LangSync />
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <AppHeader />

          <Box component='div' sx={{ flex: 1 }}>
            <Routes>
              <Route path='/' element={<Main />} />
              <Route path='/introduce' element={<Company companyData={companyData} />} />
              <Route path='/bus' element={<Bus />} />
              <Route path='/center' element={<ServiceCenter companyData={companyData} />} />
              {/* 사이트 하단 링크. 서버 WebController 에도 같은 주소가 있어야 새로고침해도 열린다 */}
              <Route path='/privacy' element={<Privacy companyData={companyData} />} />
              <Route path='/terms' element={<Terms companyData={companyData} />} />

              {/* 영문 홈페이지 (2026-11-04). 서버 WebController 에도 같은 주소, 검색로봇용 HTML 은 prerender/ */}
              <Route path='/en' element={<EnMain />} />
              <Route path='/en/bus' element={<EnBus />} />
              <Route path='/en/introduce' element={<EnCompany companyData={companyData} />} />
              <Route path='/en/center' element={<EnCenter companyData={companyData} />} />
              <Route path='/en/*' element={<Navigate to='/en' replace />} />

              {/* 공개 견적목록 */}
              {/* 주소창에 /list 만 쳐도(또는 옛 링크로 들어와도) 첫 페이지를 보여준다.
                  이 줄이 없어 /list 는 "해당 페이지를 찾을 수 없습니다" 로 떨어졌다. */}
              <Route path='/list' element={<Navigate to='/list/1' replace />} />
              <Route path='/list/:page' element={<EstimateList />} />

              {/* 나의 견적 (문자 인증 후) */}
              <Route path='/my' element={<MyEstimateSearch />} />
              <Route path='/my/list/:page' element={<MyEstimateList />} />
              <Route path='/my/list/:page/:estimateId' element={<EstimateDetail />} />

              {/* 예전 주소 호환 - 새 주소로 이동 */}
              <Route path='/service-center' element={<Navigate to='/center' replace />} />
              <Route path='/estimate/list/:page' element={<LegacyRedirect to='/list' />} />
              <Route path='/estimate/my' element={<Navigate to='/my' replace />} />
              <Route path='/estimate/my/list/:page' element={<Navigate to='/my' replace />} />
              <Route path='/estimate/my/list/:page/:estimateId' element={<Navigate to='/my' replace />} />
              <Route path='/mobile' element={<Navigate to='/' replace />} />
              <Route path='/mobile/myestimate' element={<Navigate to='/my' replace />} />
              <Route path='/mobile/myestimate/list/:page' element={<Navigate to='/my' replace />} />
              <Route path='/mobile/service-center' element={<Navigate to='/center' replace />} />

              <Route path='*' element={<NotFoundPage />} />
            </Routes>
          </Box>

          <KoreanOnly>
            <InquiryButton companyData={companyData} />
          </KoreanOnly>
          <AppFooter companyData={companyData} />
        </Box>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
